import { markdownToComposerBlocks } from "./chatFormatting";

function cleanText(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "");
}

function isBoldName(name) {
  return name === "STRONG" || name === "B";
}

function isItalicName(name) {
  return name === "EM" || name === "I";
}

function serializeInline(node) {
  let out = "";
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += cleanText(child.textContent);
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const name = child.nodeName;
    if (name === "BR") return;
    if (isBoldName(name)) {
      out += serializeWrapped(child, "**");
      return;
    }
    if (isItalicName(name)) {
      out += serializeWrapped(child, "*");
      return;
    }
    if (name === "DIV" || name === "P") {
      if (out && !out.endsWith("\n")) out += "\n";
      out += serializeInline(child);
      return;
    }
    out += serializeInline(child);
  });
  return out;
}

function serializeWrapped(el, marker) {
  const inner = serializeInline(el);
  if (!inner) return "";
  return `${marker}${inner}${marker}`;
}

function pushLine(blocks, value) {
  blocks.push(cleanText(value).replace(/\n/g, " "));
}

function renumberList(list) {
  if (!list || list.nodeName !== "OL") return;
  let n = 1;
  [...list.children].forEach((li) => {
    if (li.nodeName !== "LI") return;
    li.setAttribute("value", String(n));
    n += 1;
  });
}

function cleanupList(list) {
  if (
    list &&
    (list.nodeName === "UL" || list.nodeName === "OL") &&
    !list.querySelector("li")
  ) {
    list.remove();
  }
}

function unwrapElement(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  el.remove();
}

function appendInline(parent, nodes, doc) {
  nodes.forEach((node) => {
    if (!node) return;
    if (node.type === "text") {
      if (node.text) parent.appendChild(doc.createTextNode(node.text));
      return;
    }
    if (node.type === "bold" || node.type === "italic") {
      const el = doc.createElement(node.type === "bold" ? "strong" : "em");
      appendInline(el, node.children || [], doc);
      if (!el.childNodes.length) return;
      parent.appendChild(el);
      return;
    }
    if (node.type === "mention") {
      const el = doc.createElement("span");
      const isGroup = String(node.userId) === "0";
      el.className = `mention${isGroup ? " group" : ""}`;
      el.dataset.mention = "true";
      el.setAttribute("contenteditable", "false");
      el.textContent = node.text;
      parent.appendChild(el);
      return;
    }
    if (node.type === "link") {
      if (node.text) parent.appendChild(doc.createTextNode(node.text));
    }
  });
}

function emptyLine(doc, tag) {
  const el = doc.createElement(tag || "div");
  el.appendChild(doc.createElement("br"));
  return el;
}

function ensurePlaceholder(el) {
  if (el.childNodes.length) return;
  el.appendChild(el.ownerDocument.createElement("br"));
}

export function renderMarkdownInto(root, markdown, mentions) {
  const doc = root.ownerDocument;
  const blocks = markdownToComposerBlocks(markdown, mentions);
  root.replaceChildren();
  if (!blocks.length) {
    root.appendChild(emptyLine(doc));
    return;
  }
  blocks.forEach((block) => {
    if (block.type === "line") {
      const div = doc.createElement("div");
      appendInline(div, block.children, doc);
      ensurePlaceholder(div);
      root.appendChild(div);
      return;
    }
    const list = doc.createElement(block.type === "bullet" ? "ul" : "ol");
    block.items.forEach((item) => {
      const li = doc.createElement("li");
      if (block.type === "numbered") {
        li.setAttribute("value", String(item.value));
      }
      appendInline(li, item.children, doc);
      ensurePlaceholder(li);
      list.appendChild(li);
    });
    root.appendChild(list);
  });
}

export function serializeComposer(root) {
  if (!root) return "";
  const blocks = [];
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = cleanText(node.textContent);
      if (text) pushLine(blocks, text);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const name = node.nodeName;
    if (name === "UL") {
      [...node.children].forEach((li) => {
        if (li.nodeName === "LI") pushLine(blocks, `- ${serializeInline(li)}`);
      });
      return;
    }
    if (name === "OL") {
      let n = 1;
      [...node.children].forEach((li) => {
        if (li.nodeName !== "LI") return;
        const value = li.getAttribute("value");
        const num = value ? Number(value) : n;
        pushLine(blocks, `${num}. ${serializeInline(li)}`);
        n = num + 1;
      });
      return;
    }
    if (name === "DIV" || name === "P" || name === "LI") {
      pushLine(blocks, serializeInline(node));
      return;
    }
    [...node.childNodes].forEach(walk);
  };
  [...root.childNodes].forEach(walk);
  return blocks.join("\n");
}

function selectionIn(root) {
  const sel = root.ownerDocument.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;
  return { sel, range };
}

export function textBeforeCaret(root) {
  const found = selectionIn(root);
  if (!found) return "";
  const range = found.range.cloneRange();
  range.setStart(root, 0);
  return range.toString();
}

function currentLine(root) {
  const found = selectionIn(root);
  if (!found) return root.querySelector("div, li");
  let node = found.range.startContainer;
  while (node && node !== root) {
    if (node.nodeName === "LI") return node;
    if (
      (node.nodeName === "DIV" || node.nodeName === "P") &&
      node.parentNode === root
    ) {
      return node;
    }
    node = node.parentNode;
  }
  return root.querySelector("div, li");
}

function placeCaret(el, atStart) {
  const sel = el.ownerDocument.getSelection();
  if (!sel) return;
  const range = el.ownerDocument.createRange();
  range.selectNodeContents(el);
  range.collapse(Boolean(atStart));
  sel.removeAllRanges();
  sel.addRange(range);
}

// Deleting everything natively leaves one empty bullet behind; reset to a plain line.
export function clearLoneEmptyListItem(root) {
  const items = root.querySelectorAll("li");
  if (items.length !== 1 || !isEmptyLine(root)) return false;
  root.replaceChildren(emptyLine(root.ownerDocument));
  placeCaret(root.firstChild, true);
  return true;
}

function ensureLine(root) {
  if (root.querySelector("div, li")) return;
  root.appendChild(emptyLine(root.ownerDocument));
}

export function insertTextAtCaret(root, text) {
  if (!root || !text) return;
  ensureLine(root);
  const doc = root.ownerDocument;
  const found = selectionIn(root);
  if (!found) {
    const last = root.querySelector("li:last-child, div:last-of-type") || root;
    const target =
      last.nodeName === "UL" || last.nodeName === "OL"
        ? last.lastElementChild
        : last;
    const brs = [...target.childNodes].filter((node) => node.nodeName === "BR");
    if (brs.length && !cleanText(target.textContent))
      brs.forEach((br) => br.remove());
    const node = doc.createTextNode(text);
    target.appendChild(node);
    placeCaret(target, false);
    return;
  }
  const { sel, range } = found;
  range.deleteContents();
  const node = doc.createTextNode(text);
  range.insertNode(node);
  const parent = node.parentNode;
  if (parent && cleanText(parent.textContent)) {
    [...parent.childNodes].forEach((child) => {
      if (child.nodeName === "BR") child.remove();
    });
  }
  const after = doc.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
}

function isEmptyLine(line) {
  return (
    !cleanText(line.textContent).trim() &&
    !line.querySelector("span[data-mention]")
  );
}

function liToPlainLine(li) {
  const div = convertItemsToDivs([li]);
  if (div) placeCaret(div, true);
  return div;
}

export function insertComposerNewline(root) {
  if (!root) return;
  ensureLine(root);
  const found = selectionIn(root);
  const line = currentLine(root);
  if (!found || !line) return;
  if (line.nodeName === "LI" && isEmptyLine(line)) {
    liToPlainLine(line);
    return;
  }
  const doc = root.ownerDocument;
  const after = doc.createRange();
  after.selectNodeContents(line);
  after.setStart(found.range.endContainer, found.range.endOffset);
  const frag = after.extractContents();
  const next = doc.createElement(line.nodeName === "LI" ? "li" : "div");
  if (
    cleanText(frag.textContent) ||
    frag.querySelector("span, strong, em, b, i")
  ) {
    next.appendChild(frag);
  }
  ensurePlaceholder(next);
  if (
    !cleanText(line.textContent) &&
    !line.querySelector("span, strong, em, b, i")
  ) {
    line.replaceChildren(doc.createElement("br"));
  }
  line.after(next);
  if (next.parentNode && next.parentNode.nodeName === "OL") {
    renumberList(next.parentNode);
  }
  placeCaret(next, true);
}

/**
 * Backspace with the caret at the start of a list item turns that item
 * back into a plain line. Returns true when it handled the key.
 */
export function exitListAtLineStart(root) {
  if (!root) return false;
  const found = selectionIn(root);
  if (!found || !found.range.collapsed) return false;
  const line = currentLine(root);
  if (!line || line.nodeName !== "LI") return false;
  const before = root.ownerDocument.createRange();
  before.selectNodeContents(line);
  before.setEnd(found.range.startContainer, found.range.startOffset);
  if (cleanText(before.toString())) return false;
  if (before.cloneContents().querySelector("span[data-mention]")) return false;
  liToPlainLine(line);
  return true;
}

const CARET_MARK = "\uE000";

/**
 * Splices markdown into the composer at the caret and re-renders, so pasted
 * bold/italic/lists show as styling and match what gets sent.
 */
export function insertMarkdownAtCaret(root, markdown, mentionsFor) {
  if (!root || !markdown) return;
  ensureLine(root);
  if (!serializeComposer(root)) {
    root.replaceChildren(emptyLine(root.ownerDocument));
    placeCaret(root.firstChild, true);
  }
  const found = selectionIn(root);
  if (found) {
    found.range.deleteContents();
    found.range.insertNode(root.ownerDocument.createTextNode(CARET_MARK));
  } else {
    insertTextAtCaret(root, CARET_MARK);
  }
  const current = serializeComposer(root);
  const at = current.indexOf(CARET_MARK);
  const next =
    at === -1
      ? current + markdown + CARET_MARK
      : current.slice(0, at) + markdown + CARET_MARK + current.slice(at + 1);
  const mentions = mentionsFor ? mentionsFor(next.replace(CARET_MARK, "")) : [];
  renderMarkdownInto(root, next, mentions);

  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
  );
  let node = walker.nextNode();
  while (node) {
    const index = node.textContent.indexOf(CARET_MARK);
    if (index !== -1) {
      node.textContent =
        node.textContent.slice(0, index) +
        node.textContent.slice(index + CARET_MARK.length);
      const parent = node.parentNode;
      const sel = root.ownerDocument.getSelection();
      const range = root.ownerDocument.createRange();
      if (node.textContent) {
        range.setStart(node, index);
      } else {
        const holder = parent;
        node.remove();
        if (holder && !holder.childNodes.length) ensurePlaceholder(holder);
        range.selectNodeContents(holder || root);
        range.collapse(false);
      }
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    node = walker.nextNode();
  }
}

function styleFlags(el, inherited) {
  let { bold, italic } = inherited;
  const name = el.nodeName;
  if (name === "B" || name === "STRONG" || /^H[1-6]$/.test(name)) bold = true;
  if (name === "I" || name === "EM") italic = true;
  const weight = el.style?.fontWeight;
  if (weight) {
    const numeric = Number(weight);
    if (weight === "bold" || weight === "bolder" || numeric >= 600) bold = true;
    else if (weight === "normal" || weight === "lighter" || numeric) {
      bold = false;
    }
  }
  const fontStyle = el.style?.fontStyle;
  if (fontStyle === "italic" || fontStyle === "oblique") italic = true;
  else if (fontStyle === "normal") italic = false;
  return { bold, italic };
}

const SKIPPED = new Set([
  "SCRIPT",
  "STYLE",
  "META",
  "TITLE",
  "HEAD",
  "LINK",
  "TEMPLATE",
]);

const BLOCKS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "PRE",
  "SECTION",
  "ARTICLE",
  "HEADER",
  "FOOTER",
  "TR",
  "TABLE",
  "DT",
  "DD",
]);

function runsToMarkdown(runs) {
  const merged = [];
  runs.forEach((run) => {
    const last = merged[merged.length - 1];
    if (last && last.bold === run.bold && last.italic === run.italic) {
      last.text += run.text;
    } else {
      merged.push({ ...run });
    }
  });
  return merged
    .map((run) => {
      if (!run.bold && !run.italic) return run.text;
      const match = run.text.match(/^(\s*)([\s\S]*?)(\s*)$/);
      const [, lead, core, trail] = match;
      if (!core) return run.text;
      const marker = run.bold && run.italic ? "***" : run.bold ? "**" : "*";
      return `${lead}${marker}${core}${marker}${trail}`;
    })
    .join("")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/**
 * Converts clipboard HTML (Google Docs, Word, web pages, our own chat) into
 * the chat markdown subset: bold, italic, bullets, numbered items, lines.
 */
export function htmlToChatMarkdown(html) {
  if (!html || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const lines = [];
  const lists = [];
  let line = null;

  const startLine = (prefix = "") => {
    line = { prefix, runs: [] };
    lines.push(line);
  };
  const endLine = () => {
    line = null;
  };

  const walk = (node, style) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent
        .replace(/\u200b/g, "")
        .replace(/[\s\u00a0]+/g, " ");
      if (!text) return;
      if (!line) {
        if (!text.trim()) return;
        startLine();
      }
      line.runs.push({ text, bold: style.bold, italic: style.italic });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const name = node.nodeName;
    if (SKIPPED.has(name)) return;
    if (name === "BR") {
      if (line) endLine();
      else {
        startLine();
        endLine();
      }
      return;
    }
    const next = styleFlags(node, style);
    if (name === "UL" || name === "OL") {
      endLine();
      const start = Number(node.getAttribute("start")) || 1;
      lists.push({ ordered: name === "OL", n: start });
      node.childNodes.forEach((child) => walk(child, next));
      lists.pop();
      endLine();
      return;
    }
    if (name === "LI") {
      endLine();
      const list = lists[lists.length - 1];
      let prefix = "- ";
      if (list && list.ordered) {
        const value = Number(node.getAttribute("value"));
        if (value) list.n = value;
        prefix = `${list.n}. `;
        list.n += 1;
      }
      startLine(prefix);
      node.childNodes.forEach((child) => walk(child, next));
      endLine();
      return;
    }
    if (BLOCKS.has(name)) {
      // Google Docs and Word wrap list item text in <p>; keep it on the item line.
      if (!line || line.runs.length) endLine();
      node.childNodes.forEach((child) => walk(child, next));
      endLine();
      return;
    }
    node.childNodes.forEach((child) => walk(child, next));
  };

  walk(doc.body, { bold: false, italic: false });

  const out = lines.map((item) => {
    const content = runsToMarkdown(item.runs);
    if (item.prefix) return `${item.prefix}${content}`;
    return content;
  });
  while (out.length && !out[0].trim()) out.shift();
  while (out.length && !out[out.length - 1].trim()) out.pop();
  return out.join("\n");
}

export function insertPlainText(root, text) {
  const parts = String(text)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");
  insertTextAtCaret(root, parts[0]);
  parts.slice(1).forEach((part) => {
    insertComposerNewline(root);
    if (part) insertTextAtCaret(root, part);
  });
}

/**
 * Programmatic edits do not move the scroll position, so keep the caret
 * visible inside the scrollable composer.
 */
export function scrollCaretIntoView(root) {
  if (!root) return;
  const found = selectionIn(root);
  if (!found) return;
  let rect = found.range.getBoundingClientRect?.();
  if (!rect || (!rect.height && !rect.width)) {
    let node = found.range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
    rect = node?.getBoundingClientRect?.();
  }
  if (!rect || !rect.height) return;
  const box = root.getBoundingClientRect();
  if (rect.bottom > box.bottom) root.scrollTop += rect.bottom - box.bottom;
  else if (rect.top < box.top) root.scrollTop -= box.top - rect.top;
}

function domPointAtTextOffset(root, offset) {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
  );
  let remaining = offset;
  let node = walker.nextNode();
  while (node) {
    const len = node.textContent.length;
    if (remaining <= len) return { node, offset: remaining };
    remaining -= len;
    node = walker.nextNode();
  }
  return null;
}

export function replaceActiveMention(root, name) {
  const found = selectionIn(root);
  if (!found) return false;
  const before = textBeforeCaret(root);
  const at = before.lastIndexOf("@");
  if (at < 0) return false;
  const point = domPointAtTextOffset(root, at);
  if (!point) return false;
  const doc = root.ownerDocument;
  const replace = doc.createRange();
  replace.setStart(point.node, point.offset);
  replace.setEnd(found.range.endContainer, found.range.endOffset);
  replace.deleteContents();
  const span = doc.createElement("span");
  span.className = name === "group" ? "mention group" : "mention";
  span.dataset.mention = "true";
  span.setAttribute("contenteditable", "false");
  span.textContent = `@${name}`;
  const space = doc.createTextNode(" ");
  const frag = doc.createDocumentFragment();
  frag.appendChild(span);
  frag.appendChild(space);
  replace.insertNode(frag);
  const after = doc.createRange();
  after.setStartAfter(space);
  after.collapse(true);
  found.sel.removeAllRanges();
  found.sel.addRange(after);
  return true;
}

function ancestorTag(node, tag, root) {
  let current =
    node && node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
  while (current && current !== root) {
    if (
      (tag === "STRONG" && isBoldName(current.nodeName)) ||
      (tag === "EM" && isItalicName(current.nodeName))
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

function selectContents(el) {
  const sel = el.ownerDocument.getSelection();
  if (!sel) return;
  const range = el.ownerDocument.createRange();
  range.selectNodeContents(el);
  sel.removeAllRanges();
  sel.addRange(range);
}

function wrapRange(range, el) {
  const doc = el.ownerDocument;
  try {
    range.surroundContents(el);
  } catch (error) {
    const frag = range.extractContents();
    el.appendChild(frag);
    range.insertNode(el);
  }
  if (!cleanText(el.textContent) && !el.querySelector("span")) {
    unwrapElement(el);
    return;
  }
  selectContents(el);
  const parent = el.parentNode;
  if (parent) {
    [...parent.childNodes].forEach((child) => {
      if (child.nodeName === "BR") child.remove();
    });
  }
}

function unwrapPartial(wrapper, range) {
  const doc = wrapper.ownerDocument;
  const afterRange = doc.createRange();
  afterRange.selectNodeContents(wrapper);
  afterRange.setStart(range.endContainer, range.endOffset);
  const afterFrag = afterRange.extractContents();

  const midRange = doc.createRange();
  midRange.setStart(range.startContainer, range.startOffset);
  midRange.setEnd(range.endContainer, range.endOffset);
  const mid = midRange.extractContents();

  const afterEl = doc.createElement(wrapper.nodeName.toLowerCase());
  if (
    cleanText(afterFrag.textContent) ||
    afterFrag.querySelector("span, strong, em, b, i")
  ) {
    afterEl.appendChild(afterFrag);
  }
  const midNodes = [...mid.childNodes];
  if (afterEl.childNodes.length) wrapper.after(afterEl);
  if (midNodes.length) wrapper.after(...midNodes);
  if (
    !cleanText(wrapper.textContent) &&
    !wrapper.querySelector("span, strong, em, b, i")
  ) {
    wrapper.remove();
  }
  if (midNodes.length) {
    const sel = doc.getSelection();
    const next = doc.createRange();
    next.setStartBefore(midNodes[0]);
    next.setEndAfter(midNodes[midNodes.length - 1]);
    sel.removeAllRanges();
    sel.addRange(next);
  }
}

function toggleInlineManually(root, tag) {
  const found = selectionIn(root);
  if (!found || found.range.collapsed) {
    const doc = root.ownerDocument;
    try {
      doc.execCommand("styleWithCSS", false, false);
      doc.execCommand(tag === "STRONG" ? "bold" : "italic");
    } catch (error) {
      // jsdom has no editing host commands; a collapsed caret stays put.
    }
    return;
  }
  const { range } = found;
  const startWrap = ancestorTag(range.startContainer, tag, root);
  const endWrap = ancestorTag(range.endContainer, tag, root);
  if (startWrap && startWrap === endWrap) {
    const full = root.ownerDocument.createRange();
    full.selectNodeContents(startWrap);
    const coversAll =
      range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
      range.compareBoundaryPoints(Range.END_TO_END, full) >= 0;
    if (coversAll) unwrapElement(startWrap);
    else unwrapPartial(startWrap, range);
    return;
  }
  wrapRange(range, root.ownerDocument.createElement(tag.toLowerCase()));
}

export function toggleComposerInline(root, format) {
  if (!root) return;
  toggleInlineManually(root, format === "bold" ? "STRONG" : "EM");
}

function selectedLines(root) {
  const found = selectionIn(root);
  if (!found || found.range.collapsed) {
    const line = currentLine(root);
    return line ? [line] : [];
  }
  const { range } = found;
  const lines = [];
  const visit = (el) => {
    if (
      (el.nodeName === "DIV" || el.nodeName === "P") &&
      el.parentNode === root
    ) {
      lines.push(el);
      return;
    }
    if (el.nodeName === "UL" || el.nodeName === "OL") {
      [...el.children].forEach((child) => {
        if (child.nodeName === "LI") lines.push(child);
      });
    }
  };
  [...root.children].forEach(visit);
  const hit = lines.filter((line) => lineIntersectsRange(range, line, root));
  return hit.length ? hit : currentLine(root) ? [currentLine(root)] : [];
}

function lineIntersectsRange(range, line, root) {
  if (typeof range.intersectsNode === "function") {
    try {
      if (range.intersectsNode(line)) return true;
    } catch (error) {
      // Some selections are easier to compare as child offsets.
    }
  }
  if (
    line.contains(range.startContainer) ||
    line.contains(range.endContainer)
  ) {
    return true;
  }
  if (range.startContainer === root && range.endContainer === root) {
    const index = [...root.childNodes].indexOf(
      line.parentNode === root ? line : line.parentNode,
    );
    return range.startOffset <= index && index < range.endOffset;
  }
  return false;
}

function attachToList(li, listTag) {
  const doc = li.ownerDocument;
  const prev = li.previousElementSibling;
  if (prev && prev.nodeName === listTag) {
    prev.appendChild(li);
    if (listTag === "OL") renumberList(prev);
    return;
  }
  const list = doc.createElement(listTag.toLowerCase());
  li.before(list);
  list.appendChild(li);
}

function pullLiToRoot(line, replacement) {
  const list = line.parentNode;
  const doc = line.ownerDocument;
  if (line.nextElementSibling) {
    const rest = doc.createElement(list.nodeName.toLowerCase());
    while (line.nextElementSibling) rest.appendChild(line.nextElementSibling);
    list.after(rest);
    if (rest.nodeName === "OL") renumberList(rest);
  }
  list.after(replacement);
}

function convertLinesToList(lines, listTag) {
  let last = null;
  lines.forEach((line, index) => {
    const doc = line.ownerDocument;
    const li = doc.createElement("li");
    while (line.firstChild) li.appendChild(line.firstChild);
    ensurePlaceholder(li);
    if (listTag === "OL") li.setAttribute("value", String(index + 1));

    if (line.nodeName === "LI") {
      const oldList = line.parentNode;
      if (oldList && oldList.nodeName === listTag) {
        line.replaceWith(li);
        if (listTag === "OL") renumberList(oldList);
        last = li;
        return;
      }
      pullLiToRoot(line, li);
      line.remove();
      cleanupList(oldList);
      attachToList(li, listTag);
      last = li;
      return;
    }

    line.replaceWith(li);
    attachToList(li, listTag);
    last = li;
  });
  return last;
}

function convertItemsToDivs(lines) {
  let last = null;
  [...lines].reverse().forEach((li) => {
    if (li.nodeName !== "LI") return;
    const doc = li.ownerDocument;
    const div = doc.createElement("div");
    while (li.firstChild) div.appendChild(li.firstChild);
    ensurePlaceholder(div);
    const list = li.parentNode;
    pullLiToRoot(li, div);
    li.remove();
    cleanupList(list);
    if (!last) last = div;
  });
  return last;
}

export function toggleComposerList(root, format) {
  if (!root) return;
  ensureLine(root);
  const lines = selectedLines(root);
  if (!lines.length) return;
  const listTag = format === "bullet" ? "UL" : "OL";
  const everyAlready = lines.every(
    (line) => line.nodeName === "LI" && line.parentNode?.nodeName === listTag,
  );
  const last = everyAlready
    ? convertItemsToDivs(lines)
    : convertLinesToList(lines, listTag);
  if (last && last.isConnected) placeCaret(last, false);
}
